import { TranslateService } from '@shared/translate/translate.service';
import { PPSSState } from '@types';
import { ColGroupDef } from '@ag-grid-community/core';
import { PpssPerformanceComponent } from '../../ppss/ppss-performance/ppss-performance.component';
import { AgGridHelperService } from '../../services/ag-grid-helper.service';

/**
 * Return default performance column definitions for South Africa(ZA).
 * @param ppssTab ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 * @param translateService translate service instance
 */
export function getDefaultPerformanceColumnDefsForZA(
  ppssTab: PpssPerformanceComponent,
  state: PPSSState,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: 'FUND',
      children: [agGridHelperService.getFundNameColDef(true)],
    },
    {
      groupId: 'CURRENCY',
      children: [agGridHelperService.getCurrencyColDef()],
    },
    {
      groupId: 'NAV',
      headerName: `${translateService.instant('pds.PERF_AS_OF_DATE')} ${
        ppssTab.navAsOfDate
      }`,
      headerClass: 'small text-grey1',
      children: [
        agGridHelperService.getNavValueColDef(),
        agGridHelperService.getNavChangeValueColDef(),
      ],
    },
    {
      groupId: 'PERFORMANCE',
      // to add asOfDate in mobile view
      headerValueGetter: () =>
        `${translateService.instant('pds.PERF_AS_OF_DATE')} ${
          ppssTab.performanceAsOf
        }`,
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: {
        perfTable: ppssTab,
      },
      children: [
        // NGC-14199 : SOUTH AFRICA - PRE - Products - Performance Currency drop down cut off switching to Annualised
        // increasing width for annualised coldefs so that performance currency drop down doesn't cuts off when switching
        // from cumulative -> annualised
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          state.config.suppressDetails,
          { minWidth: 105 }
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef({ minWidth: 105 }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef({ minWidth: 105 }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef({ minWidth: 105 }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          state.config.suppressDetails,
          { minWidth: 160 }
        ),
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          state.config.suppressDetails,
          { minWidth: 105 }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef({ minWidth: 105 }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef({ minWidth: 105 }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef({ minWidth: 105 }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          state.config.suppressDetails,
          { minWidth: 160 }
        ),
        agGridHelperService.getCalendarYearMonthlyYtdColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getCalendarYearMonthly1YrColDef(
          state.config.suppressDetails,
          {
            headerName: ppssTab.calendarYearMonthlyLabels['calendar_year-1yr'],
          }
        ),
        agGridHelperService.getCalendarYearMonthly2YrColDef({
          headerName: ppssTab.calendarYearMonthlyLabels['calendar_year-2yr'],
        }),
        agGridHelperService.getCalendarYearMonthly3YrColDef({
          headerName: ppssTab.calendarYearMonthlyLabels['calendar_year-3yr'],
        }),
        agGridHelperService.getCalendarYearMonthly4YrColDef({
          headerName: ppssTab.calendarYearMonthlyLabels['calendar_year-4yr'],
        }),
        agGridHelperService.getCalendarYearMonthly5YrColDef({
          headerName: ppssTab.calendarYearMonthlyLabels['calendar_year-5yr'],
        }),
        agGridHelperService.getCalendarYearQuarterlyYtdColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getCalendarYearQuarterly1YrColDef(
          state.config.suppressDetails,
          {
            headerName:
              ppssTab.calendarYearQuarterlyLabels['calendar_year-1yr'],
          }
        ),
        agGridHelperService.getCalendarYearQuarterly2YrColDef({
          headerName: ppssTab.calendarYearQuarterlyLabels['calendar_year-2yr'],
        }),
        agGridHelperService.getCalendarYearQuarterly3YrColDef({
          headerName: ppssTab.calendarYearQuarterlyLabels['calendar_year-3yr'],
        }),
        agGridHelperService.getCalendarYearQuarterly4YrColDef({
          headerName: ppssTab.calendarYearQuarterlyLabels['calendar_year-4yr'],
        }),
        agGridHelperService.getCalendarYearQuarterly5YrColDef({
          headerName: ppssTab.calendarYearQuarterlyLabels['calendar_year-5yr'],
        }),
        agGridHelperService.getCalendarYearInceptionColDef(),
        agGridHelperService.getCumulativeMonthlyYtdColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getCumulativeMonthly1YrColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getCumulativeMonthly3YrColDef(),
        agGridHelperService.getCumulativeMonthly5YrColDef(),
        agGridHelperService.getCumulativeMonthly10YrColDef(),
        agGridHelperService.getCumulativeMonthlyInceptionColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterlyYtdColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly1YrColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getCumulativeQuarterly3YrColDef(),
        agGridHelperService.getCumulativeQuarterly5YrColDef(),
        agGridHelperService.getCumulativeQuarterly10YrColDef(),
        agGridHelperService.getCumulativeQuarterlyInceptionColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getDiscreteMonthly1YrColDef(
          state.config.suppressDetails,
          {
            headerName: ppssTab.discreteMonthlyLabels['discrete-1yr'],
          }
        ),
        agGridHelperService.getDiscreteMonthly2YrColDef({
          headerName: ppssTab.discreteMonthlyLabels['discrete-2yr'],
        }),
        agGridHelperService.getDiscreteMonthly3YrColDef({
          headerName: ppssTab.discreteMonthlyLabels['discrete-3yr'],
        }),
        agGridHelperService.getDiscreteMonthly4YrColDef({
          headerName: ppssTab.discreteMonthlyLabels['discrete-4yr'],
        }),
        agGridHelperService.getDiscreteMonthly5YrColDef({
          headerName: ppssTab.discreteMonthlyLabels['discrete-5yr'],
        }),
        agGridHelperService.getDiscreteQuarterly1YrColDef(
          state.config.suppressDetails,
          {
            headerName: ppssTab.discreteQuarterlyLabels['discrete-1yr'],
          }
        ),
        agGridHelperService.getDiscreteQuarterly2YrColDef({
          headerName: ppssTab.discreteQuarterlyLabels['discrete-2yr'],
        }),
        agGridHelperService.getDiscreteQuarterly3YrColDef({
          headerName: ppssTab.discreteQuarterlyLabels['discrete-3yr'],
        }),
        agGridHelperService.getDiscreteQuarterly4YrColDef({
          headerName: ppssTab.discreteQuarterlyLabels['discrete-4yr'],
        }),
        agGridHelperService.getDiscreteQuarterly5YrColDef({
          headerName: ppssTab.discreteQuarterlyLabels['discrete-5yr'],
        }),
        agGridHelperService.getDiscreteInceptionColDef(),
        agGridHelperService.getFundDocumentColDef(),
      ],
    },
  ];
  ppssTab.performanceParams = colGroupDefs.find(
    (colGroupDef: ColGroupDef): boolean => colGroupDef.groupId === 'PERFORMANCE'
  ).headerGroupComponentParams;
  return colGroupDefs;
}
